import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AddHealthProfessional,
  GetHealthProfessionalByID,
  UpdateHealthProfessionalById,
} from "../../../utils/apiCalls/healthProfessionals";
import {
  ConfirmDeviceRemoval,
  GetAllDeviceForAdmin,
} from "../../../utils/apiCalls/devices";

const initialValues = {
  name: "",
  userName: "",
  prefix: "",
  mobileNumber: "",
  emailId: "",
  password: "",
  workPlace: "",
  position: "",
  description: "",
};

const initialErrors = {
  name: "",
  userName: "",
  prefix: "",
  mobileNumber: "",
  emailId: "",
  password: "",
  workPlace: "",
  position: "",
  description: "",
  assignDevices: "",
};

const EMAIL_RE = /^\S+@\S+\.\S+$/;

export default function useHPForm() {
  const [errors, setErrors] = useState(initialErrors);
  const [formData, setFormData] = useState(initialValues);
  const [messages, setMessages] = useState("");
  const [openConfirmModal, setOpenConfirmModal] = useState(false); // YES / NO
  const [openResultModal, setOpenResultModal] = useState(false); // OK only
  const [removedDevicesResponse, setRemovedDevicesResponse] = useState([]); // API data
  const [isLoading, setIsLoading] = useState(false);
  const [devices, setDevices] = useState([]);
  const [assignDevices, setAssignDevices] = useState([]);
  const [numberOfUsers, setNumberOfUsers] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const data = location?.state?.rowData;
  const isEditing = !!data?.id;
  const [resetPassword, setResetPassword] = useState(false);
  const [removedDeviceIds, setRemovedDeviceIds] = useState([]);
  // Whether OK on the result modal should return to the table (a completed
  // device-removal flow) or stay put (a plain save error to retry).
  const [resultNavigatesAway, setResultNavigatesAway] = useState(false);
  const [secondaryEmails, setSecondaryEmails] = useState([]);
  const [newSecondaryEmail, setNewSecondaryEmail] = useState("");
  const [secondaryEmailError, setSecondaryEmailError] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  // Last known-good (server-saved) snapshot of everything the edit form can
  // touch, refreshed on load and after a successful save. Cancel restores
  // from this synchronously instead of re-fetching, so a slow/failed network
  // request can't leave an unsaved edit showing in the read-only view.
  const pristineRef = useRef({
    formData: initialValues,
    secondaryEmails: [],
    assignDevices: [],
    numberOfUsers: 0,
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingEmail, setEditingEmail] = useState("");
  const [onSaveSuccess, setOnSaveSuccess] = useState(null);

  const mergedDevices = useMemo(() => {
    const map = new Map();
    devices.forEach((dev) => {
      map.set(String(dev.value), { ...dev });
    });
    assignDevices.forEach((ass) => {
      const key = String(ass.value);
      const existing = map.get(key);
      if (existing) {
        map.set(key, { ...existing, slots: ass.slots });
      } else {
        map.set(key, { value: ass.value, label: ass.label, slots: ass.slots });
      }
    });
    return Array.from(map.values());
  }, [devices, assignDevices]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
    setIsDirty(true);
  };

  const handleNewSecondaryEmailChange = (value) => {
    setNewSecondaryEmail(value);
    setSecondaryEmailError("");
  };

  const handleAddSecondaryEmail = () => {
    const email = newSecondaryEmail.trim();

    if (!email) {
      setSecondaryEmailError("Please enter an email address");
      return;
    }

    if (!EMAIL_RE.test(email)) {
      setSecondaryEmailError("Please enter a valid email address");
      return;
    }

    if (secondaryEmails.includes(email)) {
      setSecondaryEmailError("This email already exists");
      return;
    }

    setSecondaryEmails([...secondaryEmails, email]);
    setNewSecondaryEmail("");
    setSecondaryEmailError("");
    setIsDirty(true);
  };

  const handleDeleteSecondaryEmail = (index) => {
    setSecondaryEmails(secondaryEmails.filter((_, i) => i !== index));
    setIsDirty(true);
  };

  const handleEditSecondaryEmail = (index) => {
    setEditingIndex(index);
    setEditingEmail(secondaryEmails[index]);
    setSecondaryEmailError("");
  };

  const handleSaveEditSecondaryEmail = (index) => {
    const email = editingEmail.trim();
    if (!email) {
      setSecondaryEmailError("Please enter an email address");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setSecondaryEmailError("Please enter a valid email address");
      return;
    }
    const isDuplicate = secondaryEmails.some(
      (e, i) => i !== index && e === email
    );
    if (isDuplicate) {
      setSecondaryEmailError("This email already exists");
      return;
    }
    const updated = [...secondaryEmails];
    updated[index] = email;
    setSecondaryEmails(updated);
    setEditingIndex(null);
    setEditingEmail("");
    setSecondaryEmailError("");
    setIsDirty(true);
  };

  const handleCancelEditSecondaryEmail = () => {
    setEditingIndex(null);
    setEditingEmail("");
    setSecondaryEmailError("");
  };

  const getAllDevicesData = async () => {
    try {
      const res = await GetAllDeviceForAdmin();
      const { data: deviceData, statusCode } = res;
      if (statusCode === 200) {
        const formattedData = deviceData.map((item) => ({
          label: item.deviceName,
          value: item.deviceId,
          slots: item.numberOfSlots,
        }));
        // Merge rather than overwrite: this can resolve after
        // getHealthProfessionalByID has already folded the HP's own
        // (already-assigned, thus excluded from this "available" list)
        // devices in, and that merge must not be lost to load order.
        setDevices((prev) => {
          const map = new Map(prev.map((d) => [String(d.value), d]));
          formattedData.forEach((device) => {
            map.set(String(device.value), device);
          });
          return Array.from(map.values());
        });
      }
    } catch (error) {
      console.error("Error fetching devices:", error?.message);
    }
  };

  useEffect(() => {
    getAllDevicesData();
  }, []);

  const getHealthProfessionalByID = async (id, isStale = () => false) => {
    try {
      const res = await GetHealthProfessionalByID(id);
      if (isStale()) return;
      const { data: hpData, message, statusCode } = res;
      if (statusCode === 200) {
        const totalSlots = Array.isArray(hpData?.deviceModel)
          ? hpData?.deviceModel
              ?.map((device) => device?.numberOfSlots || 0)
              ?.reduce((acc, current) => acc + current, 0)
          : 0;

        const mappedAssignDevices = hpData?.deviceModel.map((device) => ({
          value: device?.deviceId,
          label: device?.deviceName,
          slots: device?.numberOfSlots,
        }));
        const nextFormData = { ...hpData, assignDevices: mappedAssignDevices };
        const nextSecondaryEmails = hpData?.emails || [];

        setFormData(nextFormData);
        setAssignDevices(mappedAssignDevices);

        // GetAllDeviceForAdmin only returns devices not yet assigned to any
        // HP, so this HP's own devices need to be folded into the base pool
        // too - otherwise unchecking one drops it from both lists at once
        // and the card vanishes instead of just becoming unchecked.
        setDevices((prev) => {
          const map = new Map(prev.map((d) => [String(d.value), d]));
          (hpData?.deviceModel || []).forEach((device) => {
            const key = String(device?.deviceId);
            if (!map.has(key)) {
              map.set(key, {
                label: device?.deviceName,
                value: device?.deviceId,
                slots: device?.numberOfSlots,
              });
            }
          });
          return Array.from(map.values());
        });

        setNumberOfUsers(totalSlots);
        setSecondaryEmails(nextSecondaryEmails);
        setIsDirty(false);
        pristineRef.current = {
          formData: nextFormData,
          secondaryEmails: nextSecondaryEmails,
          assignDevices: mappedAssignDevices,
          numberOfUsers: totalSlots,
        };
      } else {
        console.error("Error fetching health professional:", message);
      }
    } catch (error) {
      if (isStale()) return;
      console.error("Error fetching health professional:", error?.message);
    }
  };

  useEffect(() => {
    let ignore = false;
    if (data) {
      getHealthProfessionalByID(data?.id, () => ignore);
    }
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const updateAssignedDevices = (updatedAssignDevices) => {
    const totalSlots = updatedAssignDevices.reduce(
      (acc, device) => acc + (device.slots || 0),
      0,
    );

    setAssignDevices(updatedAssignDevices);
    setNumberOfUsers(totalSlots);
    setFormData({
      ...formData,
      assignDevices: updatedAssignDevices,
    });
  };

  const toggleDeviceSelection = (device, isChecked) => {
    const exists = assignDevices.some(
      (d) => String(d.value) === String(device.value),
    );
    let updatedAssignDevices;
    if (isChecked && !exists) {
      updatedAssignDevices = [
        ...assignDevices,
        { value: device.value, label: device.label, slots: device.slots },
      ];
      setRemovedDeviceIds((prev) =>
        prev.filter((id) => String(id) !== String(device.value)),
      );
    } else if (!isChecked && exists) {
      updatedAssignDevices = assignDevices.filter(
        (d) => String(d.value) !== String(device.value),
      );
      setRemovedDeviceIds((prev) => {
        const next = [...prev, device.value];
        // ensure no duplicates
        return Array.from(new Set(next));
      });
    } else {
      updatedAssignDevices = assignDevices;
    }
    updateAssignedDevices(updatedAssignDevices);
    setIsDirty(true);
  };

  const validateFields = () => {
    let errObj = { ...initialErrors };

    if (!formData.name) {
      errObj.name = "This field is required";
    } else {
      errObj.name = "";
    }

    if (!formData.emailId) {
      errObj.emailId = "This field is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.emailId)) {
      errObj.emailId = "Email is invalid";
    } else {
      errObj.emailId = "";
    }

    if (isEditing && resetPassword) {
      if (!formData.password) {
        errObj.password = "This field is required";
      } else if (formData.password.length < 8) {
        errObj.password = "Password must be at least 8 characters";
      } else {
        errObj.password = "";
      }
    } else {
      errObj.password = "";
    }

    if (
      !Array.isArray(formData.assignDevices) ||
      formData.assignDevices.length === 0
    ) {
      errObj.assignDevices = "Assign at least one device";
    } else {
      errObj.assignDevices = "";
    }

    setErrors(errObj);
    const isValid = Object.values(errObj).every((x) => x === "" || x === null);
    return isValid;
  };

  const onResetPasswordClick = () => {
    if (!resetPassword) {
      // enabling reset: clear the masked value for actual editing
      setResetPassword(true);
      setFormData((prev) => ({ ...prev, password: "" }));
      setIsDirty(true);
    } else {
      // cancel reset: revert to masked/disabled state
      setResetPassword(false);
      setFormData((prev) => ({ ...prev, password: "" })); // keep actual password empty; UI will show "********"
    }
  };

  const handleSubmit = async (onSuccess) => {
    if (typeof onSuccess === "function") {
      setOnSaveSuccess(() => onSuccess);
    }
    const emailsAsStrings = secondaryEmails;

    setIsLoading(true);
    if (validateFields()) {
      const selectedDeviceIds = assignDevices.map((device) =>
        parseInt(device.value),
      );
      const basePayload = {
        name: formData.name,
        userName: null || formData.userName,
        prefix: null || formData.prefix,
        mobileNumber: null || formData.mobileNumber,
        emailId: formData.emailId,
        // password is intentionally excluded here and added back in per
        // mode below: it's required on create, but on edit it must stay
        // untouched unless the user explicitly clicked Reset, since
        // formData.password is loaded from the API's stored hash and must
        // never be echoed back as if it were a new plaintext password.
        workPlace: null || formData.workPlace,
        position: null || formData.position,
        description: formData.description,
        numberOfUsers: numberOfUsers,
        deviceId: selectedDeviceIds,
        deviceModel: assignDevices.map((d) => ({
          deviceId: parseInt(d.value),
          numberOfSlots: d.slots,
        })),
        emails: emailsAsStrings,
      };

      if (!isEditing) {
        const payload = { ...basePayload };
        payload.emails = emailsAsStrings;
        delete payload.updateUserEmailInputModel;
      }

      if (isEditing) {
        const payload = { ...basePayload };
        // Only send a password when the user explicitly opted to reset it
        // and typed a new one; otherwise leave it out so the backend keeps
        // the existing password untouched.
        if (resetPassword && formData.password) {
          payload.password = formData.password;
        }

        try {
          const res = await UpdateHealthProfessionalById(payload, data?.id);
          setIsLoading(false);
          const { statusCode, message } = res;
          if (statusCode === 200) {
            setIsDirty(false);
            pristineRef.current = {
              formData,
              secondaryEmails,
              assignDevices,
              numberOfUsers,
            };
            if (removedDeviceIds.length > 0) {
              // Confirm the device-removal cascade before leaving; navigating
              // away here would unmount the form before this modal can render.
              setMessages(
                `Healthcare professional updated. Also remove this HP's patients from the ${removedDeviceIds.length} device(s) you unassigned?`,
              );
              setOpenConfirmModal(true);
            } else {
              if (typeof onSuccess === "function") {
                onSuccess();
              } else {
                navigate("/hptable");
              }
            }
          } else {
            setResultNavigatesAway(false);
            setMessages(message);
            setOpenResultModal(true);
          }
        } catch (error) {
          setIsLoading(false);
          setResultNavigatesAway(false);
          setMessages(error?.message);
          setOpenResultModal(true);
        }
      } else {
        // ADD mode: password is required, always include it
        const payload = { ...basePayload, password: formData.password };

        try {
          const res = await AddHealthProfessional(payload);
          setIsLoading(false);
          const { statusCode, message } = res;
          if (statusCode === 200) {
            setIsDirty(false);
            navigate("/hptable");
          } else {
            setResultNavigatesAway(false);
            setMessages(message);
            setOpenResultModal(true);
          }
        } catch (error) {
          setIsLoading(false);
          setResultNavigatesAway(false);
          setMessages(error?.message);
          setOpenResultModal(true);
        }
      }
    } else {
      setIsLoading(false);
    }
  };

  const handleTable = () => {
    if (isDirty && !window.confirm("Discard unsaved changes?")) {
      return;
    }
    navigate("/hptable");
  };

  // Discards any unsaved edits (Cancel) by restoring the last known-good
  // snapshot synchronously - see pristineRef above.
  const resetForm = () => {
    setFormData(pristineRef.current.formData);
    setSecondaryEmails(pristineRef.current.secondaryEmails);
    setAssignDevices(pristineRef.current.assignDevices);
    setNumberOfUsers(pristineRef.current.numberOfUsers);
    setErrors(initialErrors);
    setEditingIndex(null);
    setEditingEmail("");
    setSecondaryEmailError("");
    setNewSecondaryEmail("");
    setResetPassword(false);
    setRemovedDeviceIds([]);
    setIsDirty(false);
  };

  const handleYesClick = async () => {
    if (!data?.id) {
      console.error("No healthProfessional id found in route data");
      return;
    }
    if (!removedDeviceIds.length) {
      console.warn("No removed devices to send");
      setOpenConfirmModal(false);
      return;
    }

    setIsLoading(true);
    const payload = {
      healthProfessionalId: Number(data.id),
      deviceIds: removedDeviceIds.map((id) => Number(id)),
    };

    try {
      const res = await ConfirmDeviceRemoval(payload);
      setIsLoading(false);
      const { statusCode, message, data: apiData } = res;

      if (statusCode === 200) {
        setMessages(message || "Devices removed successfully");
        setRemovedDevicesResponse(apiData || []);
        setResultNavigatesAway(true);
        setOpenConfirmModal(false); // close YES/NO modal
        setOpenResultModal(true); // open OK modal
      } else {
        setResultNavigatesAway(false);
        setMessages(message || "Something went wrong");
        setOpenConfirmModal(false);
        setOpenResultModal(true);
      }
    } catch (error) {
      setIsLoading(false);
      setResultNavigatesAway(false);
      setMessages(error?.message || "Something went wrong");
      setOpenConfirmModal(false);
      setOpenResultModal(true);
    }
  };

  const handleNoClick = () => {
    if (data?.id) {
      getHealthProfessionalByID(data.id);
    }
    setRemovedDeviceIds([]);
    setOpenConfirmModal(false);
  };

  const handleResultOk = () => {
    setOpenResultModal(false);
    setRemovedDeviceIds([]);
    setRemovedDevicesResponse([]);

    if (resultNavigatesAway) {
      if (typeof onSaveSuccess === "function") {
        onSaveSuccess();
      } else {
        navigate("/hptable");
      }
    } else if (data?.id) {
      getHealthProfessionalByID(data.id);
    }
  };

  return {
    errors,
    formData,
    messages,
    openConfirmModal,
    openResultModal,
    removedDevicesResponse,
    isLoading,
    assignDevices,
    numberOfUsers,
    data,
    isEditing,
    resetPassword,
    secondaryEmails,
    newSecondaryEmail,
    secondaryEmailError,
    mergedDevices,
    isDirty,
    editingIndex,
    editingEmail,
    setEditingEmail,
    handleSaveEditSecondaryEmail,
    handleCancelEditSecondaryEmail,
    setOpenConfirmModal,
    setOpenResultModal,
    handleChange,
    handleNewSecondaryEmailChange,
    handleAddSecondaryEmail,
    handleDeleteSecondaryEmail,
    handleEditSecondaryEmail,
    toggleDeviceSelection,
    onResetPasswordClick,
    handleSubmit,
    handleTable,
    resetForm,
    handleYesClick,
    handleNoClick,
    handleResultOk,
  };
}
